import { defineStore } from 'pinia';
import app from '@/main.js';
import {
    buildAuditOverviewPanelQuery,
    clearAuditOverviewPanelQuery,
    getAuditSummaryPanelQueryFromType,
    isAuditOverviewRoute,
    isAuditSummaryPanelQuery,
    pushAuditOverviewRoute,
} from '@/components/audits/auditOverviewRouteUtils.js';

const TEMPLATE_CONTENT_PANEL_TYPES = ['viewTemplate', 'editTemplate'];
const OVERVIEW_SYNC_PANEL_TYPES = [
    'viewSummary',
    'editSummary',
    ...TEMPLATE_CONTENT_PANEL_TYPES,
];

function getAuditRoutePanelQuery(panelType) {
    return TEMPLATE_CONTENT_PANEL_TYPES.includes(panelType) ? 'template' : null;
}

export const useAuditStore = defineStore('audits', {
    state: () => ({
        panelTitle: '',
        panelType: 'perform',
        isPanelVisible: false,
        audit: null,
        routeHashSynced: false,
        auditHistoryVersionKey: 0,
        auditScheduleVersionKey: 0,
        auditTemplateVersionKey: 0,
    }),
    actions: {
        syncAuditRoute(type, audit) {
            const router = app.config.globalProperties.$router;
            const currentRoute = router.currentRoute.value;

            if (!audit?.hash) {
                return;
            }

            if (currentRoute.name === 'Audits') {
                const panelQuery = getAuditRoutePanelQuery(type);
                const query = { ...currentRoute.query };
                delete query.panel;
                delete query.type;

                if (panelQuery) {
                    query.panel = panelQuery;
                }

                const alreadySynced =
                    currentRoute.params?.hash === audit.hash &&
                    (currentRoute.query?.panel || null) === panelQuery;

                if (!alreadySynced) {
                    router.push({
                        name: 'Audits',
                        params: { hash: audit.hash },
                        query,
                    });
                }

                this.routeHashSynced = true;
                return;
            }

            if (
                isAuditOverviewRoute(currentRoute) &&
                OVERVIEW_SYNC_PANEL_TYPES.includes(type)
            ) {
                const panelQuery = getAuditSummaryPanelQueryFromType(type);
                const query = buildAuditOverviewPanelQuery(
                    currentRoute.query,
                    'auditSummary',
                    panelQuery ? { panel: panelQuery } : {}
                );
                const alreadySynced =
                    (currentRoute.query?.panel || null) === panelQuery;

                if (!alreadySynced) {
                    pushAuditOverviewRoute(router, currentRoute, { query });
                }

                this.routeHashSynced = true;
            }
        },

        openPanel(type, audit = null) {
            const t =
                app.config.globalProperties.$t ||
                ((key) => key);
            this.panelType = type;
            this.isPanelVisible = true;
            this.audit = audit;

            if (audit?.hash && OVERVIEW_SYNC_PANEL_TYPES.includes(type)) {
                this.syncAuditRoute(type, audit);
            }

            const titles = {
                viewTemplate:
                    audit?.name ?? t('audits.panels.panelTitles.viewAudit'),
                performTemplate: t('audits.panels.panelTitles.performAudit'),
                editTemplate: t('audits.panels.panelTitles.editAudit'),
                viewSummary:
                    audit?.name ?? t('audits.panels.panelTitles.viewAudit'),
                editSummary: t('audits.panels.panelTitles.editAudit'),
                uploadSummary: t('audits.panels.panelTitles.uploadAudit'),
                schedule: t('audits.panels.panelTitles.scheduleAudit'),
            };

            this.panelTitle = titles[type] || '';
        },

        closePanel(options = {}) {
            this.panelType = '';
            this.isPanelVisible = false;
            this.audit = null;
            this.panelTitle = '';
            this.routeHashSynced = false;

            if (options.skipRouteSync) {
                return;
            }

            const router = app.config.globalProperties.$router;
            const currentRoute = router.currentRoute.value;

            if (
                currentRoute.name === 'Audits' &&
                (currentRoute.params?.hash || currentRoute.query?.panel) &&
                currentRoute.query?.type !== 'AUDIT-TEMPLATE'
            ) {
                const query = { ...currentRoute.query };
                delete query.panel;
                router.push({
                    name: 'Audits',
                    params: { hash: null },
                    query,
                });
                return;
            }

            if (
                isAuditOverviewRoute(currentRoute) &&
                isAuditSummaryPanelQuery(currentRoute.query?.panel)
            ) {
                pushAuditOverviewRoute(router, currentRoute, {
                    query: clearAuditOverviewPanelQuery(
                        currentRoute.query,
                        'auditSummary'
                    ),
                });
            }
        },

        incrementAuditHistoryVersionKey() {
            this.auditHistoryVersionKey++;
        },

        incrementAuditScheduleVersionKey() {
            this.auditScheduleVersionKey++;
        },

        incrementAuditTemplateVersionKey() {
            this.auditTemplateVersionKey++;
        },
    },
    getters: {},
});
